import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  plan: 'basic' | 'pro' | 'enterprise';
  status: 'active' | 'suspended' | 'cancelled';
  max_users: number;
  max_products: number;
  config: Record<string, any>;
  created_at: string;
  expires_at: string | null;
}

interface TenantContextType {
  tenant: Tenant | null;
  tenantId: string | null;
  loading: boolean;
  isActive: boolean;
  isSuspended: boolean;
  refreshTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | null>(null);

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) throw new Error('useTenant must be used within TenantProvider');
  return context;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  const loadTenant = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setTenant(null);
        setLoading(false);
        return;
      }

      // Obtener el tenant_id del perfil del usuario
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('tenant_id')
        .eq('id', user.id)
        .maybeSingle();

      if (!profile?.tenant_id) {
        setTenant(null);
        setLoading(false);
        return;
      }

      // Obtener los datos del tenant
      const { data: tenantData } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', profile.tenant_id)
        .maybeSingle();

      setTenant(tenantData);
    } catch (error) {
      console.error('[TENANT] Error loading tenant:', error);
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenant();

    // Escuchar cambios de auth para recargar tenant
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event) => {
        if (event === 'SIGNED_IN') {
          loadTenant();
        } else if (event === 'SIGNED_OUT') {
          setTenant(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [loadTenant]);

  const value: TenantContextType = {
    tenant,
    tenantId: tenant?.id || null,
    loading,
    isActive: tenant?.status === 'active',
    isSuspended: tenant?.status === 'suspended',
    refreshTenant: loadTenant,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}
